// Contrast + theme helpers.
// The whole file is pure (no React, no DOM writes) except for the
// `applyTenantTheme` helper at the bottom — that's the one place we
// touch `documentElement.style` from the AuthContext theme provider.

/** Parse `#rrggbb` or `#rgb` → {r,g,b} in 0..255. Returns null on any
 *  malformed input so callers can fall through to defaults cleanly. */
export const hexToRgb = (hex) => {
  if (!hex || typeof hex !== 'string') return null;
  const s = hex.trim().replace(/^#/, '');
  if (s.length === 3) {
    return {
      r: parseInt(s[0] + s[0], 16),
      g: parseInt(s[1] + s[1], 16),
      b: parseInt(s[2] + s[2], 16),
    };
  }
  if (s.length === 6) {
    const n = parseInt(s, 16);
    if (Number.isNaN(n)) return null;
    return { r: (n >> 16) & 0xff, g: (n >> 8) & 0xff, b: n & 0xff };
  }
  return null;
};

/** WCAG relative luminance for an sRGB colour. */
const relLuminance = ({ r, g, b }) => {
  const chan = (c) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * chan(r) + 0.7152 * chan(g) + 0.0722 * chan(b);
};

/** WCAG contrast ratio between two colours as {r,g,b} triples. */
export const contrastRatio = (a, b) => {
  const la = relLuminance(a);
  const lb = relLuminance(b);
  const [hi, lo] = la > lb ? [la, lb] : [lb, la];
  return (hi + 0.05) / (lo + 0.05);
};

const WHITE = { r: 255, g: 255, b: 255 };
const BLACK = { r: 0, g: 0, b: 0 };

/** Given a hex accent, return the best text-on-fill colour (white or
 *  black) whichever hits higher contrast. Returns '#fff' or '#000'. */
export const bestTextOn = (hex) => {
  const rgb = hexToRgb(hex);
  if (!rgb) return '#ffffff';
  return contrastRatio(rgb, WHITE) >= contrastRatio(rgb, BLACK) ? '#ffffff' : '#111827';
};

/** True when white text on this fill hits 3:1 (WCAG AA for UI components
 *  + large text). Used at Sidebar render time as a runtime guard: if the
 *  tenant's accent fails against white, we fall back to the platform
 *  default instead of shipping unreadable nav.
 *  Note: uses white specifically because that's what the sidebar nav
 *  writes on top of `--brand-accent`. */
export const passesAccentContrast = (hex) => {
  const rgb = hexToRgb(hex);
  if (!rgb) return false;
  return contrastRatio(rgb, WHITE) >= 3.0;
};

/** Shared save-time contrast evaluator. Front-end mirror of the
 *  backend's `_validate_pair`. Returns `{ok: true}` on pass, or
 *  `{ok: false, severity, msg}` on fail. Blank accent = pass
 *  (means "use platform defaults").
 *
 *  RULE: accent is used only as a BUTTON FILL / active-nav / banner
 *  fill with `accent_text` (or white when blank) on top. Never raw
 *  text on white — that path uses `darkenForText(accent)`. So the
 *  only hard block is 3:1 between (accent, accent_text). */
export const evaluateAccentPair = (accentHex, accentTextHex) => {
  const accent = (accentHex || '').trim();
  const accentText = (accentTextHex || '').trim();
  if (!accent) {
    // No accent set — only validate accent_text if provided.
    if (accentText && !hexToRgb(accentText)) {
      return { ok: false, severity: 'error', msg: 'Accent-text colour must be a valid hex — use #rrggbb.' };
    }
    return { ok: true };
  }
  const rgbAccent = hexToRgb(accent);
  if (!rgbAccent) {
    return { ok: false, severity: 'error', msg: 'Not a valid hex colour — use #rrggbb.' };
  }
  let rgbText = WHITE;
  if (accentText) {
    const parsed = hexToRgb(accentText);
    if (!parsed) return { ok: false, severity: 'error', msg: 'Accent-text colour must be a valid hex — use #rrggbb.' };
    rgbText = parsed;
  }
  const fillContrast = contrastRatio(rgbAccent, rgbText);
  if (fillContrast < 3.0) {
    const chosen = accentText || '#ffffff (default)';
    return {
      ok: false, severity: 'error',
      msg: `Accent ${accent} + text ${chosen} fails WCAG AA UI-contrast (needs ≥3:1, got ${fillContrast.toFixed(2)}:1). Pick a darker accent OR set an accent-text colour that lifts the ratio (e.g. #000000 for warm tan/gold accents).`,
    };
  }
  return { ok: true };
};

/** Contrast check for text-on-white — 4.5:1 needed for AA text.
 *  We use this to reject accent colours that would be used as text
 *  (e.g. hero eyebrow copy) and instead force a darker mix. */
export const passesTextOnWhiteContrast = (hex) => {
  const rgb = hexToRgb(hex);
  if (!rgb) return false;
  return contrastRatio(rgb, WHITE) >= 4.5;
};

/** Return the accent shifted toward black until it clears 4.5:1 on
 *  white — used for tinted badge TEXT that must remain readable even
 *  when the tenant picked a pale accent. Caps at 20 iterations. */
export const darkenForText = (hex) => {
  const rgb = hexToRgb(hex);
  if (!rgb) return '#111827';
  let { r, g, b } = rgb;
  for (let i = 0; i < 20; i += 1) {
    if (contrastRatio({ r, g, b }, WHITE) >= 4.5) break;
    r = Math.max(0, Math.round(r * 0.85));
    g = Math.max(0, Math.round(g * 0.85));
    b = Math.max(0, Math.round(b * 0.85));
  }
  return `#${[r, g, b].map((n) => n.toString(16).padStart(2, '0')).join('')}`;
};

/** Rgba string with alpha for badge backgrounds/borders. */
export const rgba = (hex, alpha) => {
  const rgb = hexToRgb(hex);
  if (!rgb) return `rgba(0,0,0,${alpha})`;
  return `rgba(${rgb.r},${rgb.g},${rgb.b},${alpha})`;
};

// ── DOM applier ────────────────────────────────────────────────────
// Only called by AuthContext on login / masquerade start / masquerade
// exit. Writes brand CSS variables + typography + banner, injects
// @font-face rules for custom-hosted fonts, or clears them all.
const CSS_VAR_NAMES = [
  '--brand-accent',
  '--brand-accent-text',
  '--brand-primary',
  '--brand-primary-text',
  '--brand-badge-bg',
  '--brand-badge-text',
  '--brand-badge-border',
  '--brand-font-display',
  '--brand-font-body',
  '--brand-banner-image',
  '--brand-banner-color',
];

const FONT_STYLE_ID = 'tenant-font-face-injection';

/** Inject an @font-face style tag for tenant-hosted fonts. Called
 *  from `applyTenantTheme` — never mount directly. Strips any prior
 *  injection so tenants can flip fonts without a page reload.
 *
 *  Accepts EITHER:
 *   - the legacy single-URL shape ({displayFamily, displayUrl,
 *     bodyFamily, bodyUrl}) — emits one @font-face per family, or
 *   - a `faces` array of {family, url, weight, style} descriptors —
 *     emits one @font-face per entry so multi-weight matches work
 *     (TotalFusion Circular Std ships 5 weights × 2 styles).
 *
 *  Both may be provided together — the `faces` array wins on family
 *  overlap because the legacy fields are single-weight fallbacks. */
const applyTenantFonts = ({ displayFamily, displayUrl, bodyFamily, bodyUrl, faces = [] }) => {
  // Remove any previously injected block first — always start clean.
  const existing = document.getElementById(FONT_STYLE_ID);
  if (existing) existing.remove();

  // Extract the first quoted family name from a css font-family string
  // so `"AW Conqueror Sans", "Manrope", sans-serif` → `AW Conqueror Sans`.
  // If no quoted name is found, fall back to the raw string (before the
  // first comma). Empty input → null so we skip injection.
  const primaryFamily = (raw) => {
    if (!raw) return null;
    const m = raw.match(/"([^"]+)"/) || raw.match(/'([^']+)'/);
    if (m) return m[1];
    return raw.split(',')[0].trim() || null;
  };

  const rules = [];
  const covered = new Set();   // families satisfied by the multi-face array

  // Multi-face manifest first — one @font-face per {weight, style}.
  if (Array.isArray(faces) && faces.length) {
    for (const f of faces) {
      if (!f || !f.url || !f.family) continue;
      const weight = Number.isFinite(f.weight) ? f.weight : 400;
      const style = (f.style === 'italic') ? 'italic' : 'normal';
      rules.push(
        `@font-face { font-family: "${f.family}"; src: url("${f.url}") format("woff2"); ` +
        `font-weight: ${weight}; font-style: ${style}; font-display: swap; }`,
      );
      covered.add(f.family);
    }
  }

  // Legacy single-URL fields — only apply when the manifest didn't
  // already register that family (backwards compat for tenants who
  // haven't migrated to the multi-face shape yet).
  const displayName = primaryFamily(displayFamily);
  if (displayName && displayUrl && !covered.has(displayName)) {
    rules.push(`@font-face { font-family: "${displayName}"; src: url("${displayUrl}"); font-display: swap; }`);
  }
  const bodyName = primaryFamily(bodyFamily);
  if (bodyName && bodyUrl && !covered.has(bodyName)) {
    rules.push(`@font-face { font-family: "${bodyName}"; src: url("${bodyUrl}"); font-display: swap; }`);
  }
  if (rules.length === 0) return;
  const style = document.createElement('style');
  style.id = FONT_STYLE_ID;
  style.textContent = rules.join('\n');
  document.head.appendChild(style);
};

export const applyTenantTheme = (accentHex, logoUrl = '', iconUrl = '', typography = {}, banner = {}, accentTextOverride = '', primaryHex = '') => {
  const el = document.documentElement;
  // URLs live on data-attrs so Sidebar can read them without an extra
  // context — cleared alongside the CSS vars on logout / no-branding.
  if (logoUrl) el.dataset.brandLogoUrl = logoUrl;
  else delete el.dataset.brandLogoUrl;
  if (iconUrl) el.dataset.brandIconUrl = iconUrl;
  else delete el.dataset.brandIconUrl;
  if (banner.bannerUrl) el.dataset.brandBannerUrl = banner.bannerUrl;
  else delete el.dataset.brandBannerUrl;

  // Reset font injection every call — fresh state per tenant.
  applyTenantFonts({
    displayFamily: typography.fontFamilyDisplay || '',
    displayUrl: typography.fontUrlDisplay || '',
    bodyFamily: typography.fontFamilyBody || '',
    bodyUrl: typography.fontUrlBody || '',
    faces: Array.isArray(typography.faces) ? typography.faces : [],
  });

  // Banner vars — image (URL) or solid colour. Falls through to accent
  // as the solid fill when neither is set on the tenant.
  if (banner.bannerUrl) {
    el.style.setProperty('--brand-banner-image', `url("${banner.bannerUrl}")`);
  } else {
    el.style.removeProperty('--brand-banner-image');
  }
  if (banner.bannerColor) {
    el.style.setProperty('--brand-banner-color', banner.bannerColor);
  } else {
    el.style.removeProperty('--brand-banner-color');
  }

  if (!accentHex) {
    // Reset — remove every var so the app falls back to Yabbit defaults.
    CSS_VAR_NAMES.forEach((n) => el.style.removeProperty(n));
    // BUT still apply font vars if provided — accent + typography are
    // independent settings; a tenant can theme fonts without an accent.
    if (typography.fontFamilyDisplay) {
      el.style.setProperty('--brand-font-display', typography.fontFamilyDisplay);
    }
    if (typography.fontFamilyBody) {
      el.style.setProperty('--brand-font-body', typography.fontFamilyBody);
    }
    return;
  }
  const rgb = hexToRgb(accentHex);
  if (!rgb) {
    CSS_VAR_NAMES.forEach((n) => el.style.removeProperty(n));
    return;
  }
  el.style.setProperty('--brand-accent', accentHex);
  // If the tenant explicitly locked a text-on-accent colour (e.g.
  // TotalFusion ships black on gold), honour it verbatim. Otherwise
  // fall through to the best-contrast auto-pick (white or #111827).
  const accentText = (accentTextOverride || '').trim();
  el.style.setProperty('--brand-accent-text', accentText && hexToRgb(accentText) ? accentText : bestTextOn(accentHex));
  // Primary — the heavy fill for the scope pill + active nav item.
  // TF ships `#000000` primary + accent-gold text. When set, it wins
  // over the amber badge tokens. When blank, we leave `--brand-primary`
  // unset so the SiteScopeBadge / Sidebar fall through to their
  // existing amber/token defaults — no regression for non-configured
  // tenants. Primary-text always maps to the accent for the "reverse"
  // treatment (black button, gold writing).
  const primary = (primaryHex || '').trim();
  if (primary && hexToRgb(primary)) {
    el.style.setProperty('--brand-primary', primary);
    el.style.setProperty('--brand-primary-text', accentHex);
  } else {
    el.style.removeProperty('--brand-primary');
    el.style.removeProperty('--brand-primary-text');
  }
  el.style.setProperty('--brand-badge-bg', rgba(accentHex, 0.12));
  el.style.setProperty('--brand-badge-text', darkenForText(accentHex));
  el.style.setProperty('--brand-badge-border', rgba(accentHex, 0.35));
  if (typography.fontFamilyDisplay) {
    el.style.setProperty('--brand-font-display', typography.fontFamilyDisplay);
  } else {
    el.style.removeProperty('--brand-font-display');
  }
  if (typography.fontFamilyBody) {
    el.style.setProperty('--brand-font-body', typography.fontFamilyBody);
  } else {
    el.style.removeProperty('--brand-font-body');
  }
};
